// ============================================================================
// run-blocker.ts — the ONE sentence that says why a job is not moving
// ----------------------------------------------------------------------------
// Direction A (the Staging Sheet): the sheet's WAITING ON column and the
// detail panel's status line are the SAME string, so two surfaces can never
// phrase one row's state differently.
//
// Quote Run SPEC slice 2: the words themselves now come from the one view
// model, quote-run-view.ts (quoteRunRowWords), and so does their colour
// (words.ink). blockerFor and railStatusFor only choose which of the row's
// words to print, so the sheet, the rail and the detail panel cannot drift
// apart.
// Pure: it reads the collapsed row from collapseQueueJobs and nothing else.
// ============================================================================
import {
    canCarryOnFailedJob,
    canStartOverFailedJob,
    classifyFailedQueueRecovery,
    failedJobNeedsFreshPicks,
    failedJobNextStep,
} from '../file-explorer/import-workflow';
import type { FailedQueueRecoveryKind, ImportWorkflowState } from '../file-explorer/import-workflow';
import { isResolvedRbbQuoteTitle, splitTitlePrefix } from '../../utils/quote-title';
import { splitJobName } from './WorkspaceProjectTabs';
import {
    jobFolderName,
    quoteRunRowWords,
} from './quote-run-view';
import type { QuoteRunDisplay, QuoteRunInk, RowAction, SheetRow, ViewEngine, ViewRun } from './quote-run-view';

// The row primitives moved into the view model; existing importers keep
// reading them from here.
export {
    assignRunPositions,
    noProgressFor,
    providerWaitFor,
    rowAgeIso,
    rowFileCount,
    shortAge,
} from './quote-run-view';
export type { SheetRow } from './quote-run-view';

/** The sentence's colour: the view model's (quote-run-view QuoteRunInk). */
export type BlockerTone = QuoteRunInk;

export type Blocker = {
    text: string;
    tone: BlockerTone;
    /** Figures ride the mono + tabular-nums treatment so they land on one X. */
    mono: boolean;
};

export function ordinal(value: number): string {
    if (!Number.isFinite(value) || value < 1) return '';
    const teen = value % 100;
    if (teen >= 11 && teen <= 13) return `${value}th`;
    switch (value % 10) {
        case 1: return `${value}st`;
        case 2: return `${value}nd`;
        case 3: return `${value}rd`;
        default: return `${value}th`;
    }
}

/** The row's name: the folder name picked for the run, for the life of the row (G13). */
export function rowName(row: SheetRow): string {
    return jobFolderName(row);
}

export function rowFolder(row: SheetRow): string {
    const meta = row?.effectiveJob?.displayMeta || {};
    return String(meta.relativePath || meta.path || '').trim();
}

export type WordsOptions = {
    pendingTotal?: number;
    queueActive?: boolean;
    runRemaining?: number;
    runPosition?: number | null;
    engine?: ViewEngine | null;
    /**
     * This tab's run (A4): its marker and its earlier pages' runners say a
     * reloaded page's own dead run is not "Running in another tab". Every
     * surface passes `readOwnRun()`.
     */
    run?: ViewRun | null;
    now?: number;
};

function rowContext(row: SheetRow, options: WordsOptions) {
    const { pendingTotal = 0, queueActive = false, runRemaining = 0, runPosition = null, engine = null, run = null } = options;
    // Only rank 1 of the whole run says Next (E8). Without a run-wide rank,
    // a job is Next only when it is the one job waiting.
    const position = Number(row?.position);
    const rank = runPosition ?? (pendingTotal <= 1 && Number.isFinite(position) && position > 0 ? 1 : null);
    return {
        engine: engine || { active: queueActive },
        run,
        now: Number.isFinite(options.now) ? Number(options.now) : Date.now(),
        rank,
        runRemaining,
    };
}

/**
 * Why this job is not moving, in one short line: the row's sentence from the
 * view model, or the reason its action is disabled.
 *
 * It doubles as the printed reason for a disabled row primary (SPEC §3's
 * anti-contradiction rule): the cell sits at a fixed X on the same row, so the
 * reason is inline text on screen at rest. Never a tooltip, never a bare grey
 * button.
 */
export function blockerFor(row: SheetRow, options: WordsOptions = {}): Blocker {
    const ctx = rowContext(row, options);
    const words = quoteRunRowWords(row, ctx);
    if (!words.section) return { text: '', tone: 'slate', mono: false };
    const text = words.action?.disabled && words.reason ? words.reason : words.sentence;
    const stageFigures = words.chip === 'Writing the draft' && !words.flags.stalled;
    const countFigures = words.display === 'needs_review' && !!words.action?.disabled && /\d/.test(text);
    return {
        text,
        tone: words.ink,
        mono: stageFigures || countFigures,
    };
}

/**
 * The same job's words for the workspace rail, where the sheet's columns and
 * chips are not on screen (operator, 2026-08-20: "What's with 'Not on the run
 * list' and '1st in line' I dont understand"). So the rail prints the chip and
 * the sentence together, e.g. "Waiting to start · 8 files": the same words as
 * the sheet, standing on their own.
 */
export function railStatusFor(row: SheetRow, options: WordsOptions = {}): string {
    return railWordsFor(row, options).status;
}

/**
 * The rail's words for one job: the view's chip (the sheet's word for its
 * state) and the status line railStatusFor prints, with the view's display
 * state and colours for its dot. The rail's hover text and screen-reader
 * label use the chip, never the engine's state word, which said "Processing"
 * beside "Stopped when the page closed".
 */
export function railWordsFor(row: SheetRow, options: WordsOptions = {}): {
    chip: string;
    status: string;
    display: QuoteRunDisplay;
    palette: ImportWorkflowState;
    /** The row's one action, whose rail counterpart (Take off run) the rail offers only when it is Leave out. */
    actionKind: RowAction['kind'] | null;
} {
    const ctx = rowContext(row, options);
    const words = quoteRunRowWords(row, ctx);
    const look = { display: words.display, palette: words.palette, actionKind: words.action?.kind ?? null };
    if (!words.section) return { chip: '', status: '', ...look };
    const chip = words.chip.trim();
    const sentence = words.sentence.trim();
    if (!sentence) return { chip, status: chip, ...look };
    const firstWord = (text: string) => text.split(/\s+/)[0]!.toLowerCase();
    if (sentence.toLowerCase().includes(chip.toLowerCase()) || firstWord(sentence) === firstWord(chip)) {
        return { chip, status: sentence, ...look };
    }
    return { chip, status: `${chip} · ${sentence}`, ...look };
}

export type RetryPlan = {
    title: string;
    recoveryKind: FailedQueueRecoveryKind;
    primaryLabel: string;
    confirmText: string;
    canRecover: boolean;
    canCarryOn: boolean;
    canStartOver: boolean;
    carryOnMessage: string;
    startOverMessage: string;
    noCarryOnReason: string;
    noStartOverReason: string;
};

/**
 * The two real recoveries for a stopped job, and what each one costs. Shared
 * by the sheet's row primary and the detail panel so the two can never offer
 * different deals.
 *
 * "Start over" performs a real engine transition (re-stage the saved picks,
 * then queue). The control it replaces called onGoToBrowse() and printed an
 * instruction toast: a button that read as an action and was a signpost.
 */
export function retryPlan(row: SheetRow): RetryPlan {
    const job = row?.effectiveJob || {};
    const name = rowName(row) || 'this job';
    const recoveryKind = classifyFailedQueueRecovery(job);
    const stage = Number(job.lastErrorStage || job.currentStage);
    const stopped = Number.isFinite(stage) && stage > 0
        ? `It stopped at Stage ${stage} of 5.`
        : 'It stopped part way through.';
    const selected = Array.isArray(job.selectedFileIds) ? job.selectedFileIds : [];
    // The row's one step (failedJobNextStep) opens the job or points outside
    // Quote Run: nothing here reads the folder again, carries on or starts
    // over, because each of those meets the same refusal.
    const step = failedJobNextStep(job);
    if (step.kind === 'open-stage' || step.kind === 'elsewhere') {
        return {
            title: step.label ? `${step.label}?` : `Where to finish ${name}`,
            recoveryKind,
            primaryLabel: step.label,
            confirmText: step.label,
            canRecover: true,
            canCarryOn: false,
            canStartOver: false,
            carryOnMessage: '',
            startOverMessage: '',
            noCarryOnReason: '',
            // The elsewhere reason is the row's own, printed beside it.
            noStartOverReason: step.kind === 'open-stage' ? step.why : '',
        };
    }
    if (recoveryKind === 'restore-permission') {
        return {
            title: `Restore folder access for ${name}?`,
            recoveryKind,
            primaryLabel: 'Restore folder access',
            confirmText: 'Restore folder access',
            canRecover: true,
            canCarryOn: false,
            canStartOver: false,
            carryOnMessage: 'QuoteMaker needs access to the same queued folder. Restoring access keeps the saved intake and does not start quote generation.',
            startOverMessage: '',
            noCarryOnReason: 'The source folder must be available before intake can continue.',
            noStartOverReason: 'Restore the queued folder access to continue safely.',
        };
    }
    if (recoveryKind === 'retry-saved-intake') {
        return {
            title: `Retry the saved intake for ${name}?`,
            recoveryKind,
            primaryLabel: 'Try again',
            confirmText: 'Try again',
            canRecover: true,
            canCarryOn: false,
            canStartOver: false,
            carryOnMessage: 'The selected source files are already stored in QuoteMaker. This retries intake preparation without rereading the source folder or starting the five quote stages.',
            startOverMessage: '',
            noCarryOnReason: 'Quote generation has not started yet.',
            noStartOverReason: 'The saved intake can be retried directly.',
        };
    }
    if (recoveryKind === 'review-source') {
        return {
            title: `Review the source files for ${name}`,
            recoveryKind,
            // The row's own step: Read the files again, or Pick its files
            // again for a file the cloud refused (failedJobNextStep).
            primaryLabel: step.label || 'Read the files again',
            confirmText: step.label || 'Read the files again',
            canRecover: true,
            canCarryOn: false,
            canStartOver: false,
            carryOnMessage: 'The queued intake is incomplete or no longer matches the source folder. Review the current files in Browse, then retry from that fresh scan.',
            startOverMessage: '',
            noCarryOnReason: 'The stale intake snapshot cannot be retried safely.',
            // The panel prints this beside Read the files again, whose words
            // (operatorFailureText) already say what it does and, for a stop
            // it does not clear (restart history), where the fix is. Advice to
            // review the folder files would contradict those words.
            noStartOverReason: '',
        };
    }
    // The same predicates the row's one action reads (quote-run-view
    // failedRowAction), so the panel never offers a path the row ruled out:
    // a job that needs fresh picks stops at the same check either way.
    const canCarryOn = canCarryOnFailedJob(job);
    const canStartOver = canStartOverFailedJob(job);
    // Whether the job has picks on record, in either phase: a job whose files
    // were read (a stopped intake) carries them too (queue-pre-process-enqueue
    // enqueues with selectedFileIds), and must never be told it has none.
    const picksWouldStop = failedJobNeedsFreshPicks(job) && selected.length > 0;
    return {
        title: `Try ${name} again?`,
        recoveryKind,
        primaryLabel: canCarryOn ? 'Try again' : canStartOver ? 'Start over' : 'Pick its files again',
        confirmText: canCarryOn ? 'Carry on from where it stopped' : 'Start over',
        canRecover: canCarryOn,
        canCarryOn,
        canStartOver,
        carryOnMessage: `${stopped} Carrying on keeps the stages that finished and picks up where it stopped. It costs less than starting over.`,
        startOverMessage: `${stopped} Starting over throws this attempt away and runs all five stages again from your file picks. It spends AI budget.`,
        noCarryOnReason: 'Carrying on is not possible for this failure.',
        // Why Start over is not offered, printed beside the panel's button.
        // It names Pick its files again, so it is said only when that is the
        // button: a job that can carry on (a restored project has no saved
        // picks, queue-pre-process-enqueue.js) leads with Try again or, after
        // the same stop twice, Open Stage N, and picking its files again
        // would throw away the stages its words say are kept.
        // A job with picks whose files must be read again before it can draft
        // (its saved copy was not confirmed, or its phase cannot start over)
        // says that, never that it has no picks.
        noStartOverReason: canCarryOn
            ? ''
            : picksWouldStop
                ? 'Its saved file picks would stop at the same check. Pick its files again from the folder.'
                : selected.length > 0
                    ? 'Its files must be read again from the folder before it can draft. Pick its files again.'
                    : 'This job has no saved file picks. Pick its files again from the folder.',
    };
}
